import React, { useState, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import Card from '../common/Card';
import MiniLoader from '../common/MiniLoader';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/errors';
import { shortenAddress } from '../../utils/wallet';
import CopyButton from '../common/CopyButton';

const WALLETS = [
  { key: 'adminWallet', label: 'Admin Wallet', setter: 'updateAdminWallet' },
  { key: 'teamWallet', label: 'Team Wallet', setter: 'updateTeamWallet' },
  { key: 'routerWallet', label: 'Router Wallet', setter: 'updateRouterWallet' },
  { key: 'missingIncomeWallet', label: 'Missing Income Wallet', setter: 'updateMissingIncomeWallet' }
];

const WalletUpdater = () => {
  const { storage } = useContracts();
  const notify = useNotify();

  const [addresses, setAddresses] = useState({});
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(null);
  const [loading, setLoading] = useState(false);
  const runIdRef = useRef(0);

  const load = async () => {
    const myRun = ++runIdRef.current;
    setLoading(true);
    try {
      const results = await Promise.all(WALLETS.map((w) => storage[w.key]()));
      if (myRun !== runIdRef.current) return;
      const map = {};
      WALLETS.forEach((w, i) => { map[w.key] = results[i]; });
      setAddresses(map);
    } catch (_) { /* ignore */ }
    finally {
      if (myRun === runIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const update = async (walletDef) => {
    const newAddr = (inputs[walletDef.key] || '').trim();
    if (!ethers.utils.isAddress(newAddr)) {
      notify.error('Invalid address');
      return;
    }
    setBusy(walletDef.key);
    const loadingId = notify.loading(`Updating ${walletDef.label}...`);
    try {
      const tx = await storage[walletDef.setter](newAddr);
      notify.dismiss(loadingId);
      const mining = notify.loading('Confirming...');
      await tx.wait();
      notify.dismiss(mining);
      notify.success(`${walletDef.label} updated`);
      setInputs({ ...inputs, [walletDef.key]: '' });
      load();
    } catch (err) {
      notify.dismiss(loadingId);
      notify.error(parseError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title="Wallet Addresses" subtitle="Update destination wallets">
      {loading ? (
        <div className="text-center" style={{ padding: 20 }}><MiniLoader /></div>
      ) : (
        <div className="flex flex-col gap-md">
          {WALLETS.map((w) => (
            <div key={w.key}>
              <div className="flex flex-between mb-sm">
                <div style={{ fontWeight: 600, fontSize: 13 }}>{w.label}</div>
                <div className="flex items-center gap-sm">
                  <span className="font-mono text-muted" style={{ fontSize: 12 }}>
                    {addresses[w.key] ? shortenAddress(addresses[w.key], 6) : '-'}
                  </span>
                  {addresses[w.key] && <CopyButton text={addresses[w.key]} label="Copy" />}
                </div>
              </div>
              <div className="flex gap-sm">
                <input
                  className="input font-mono"
                  style={{ fontSize: 13 }}
                  placeholder="0x..."
                  value={inputs[w.key] || ''}
                  onChange={(e) => setInputs({ ...inputs, [w.key]: e.target.value })}
                />
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => update(w)}
                  disabled={busy === w.key || !inputs[w.key]}
                >
                  {busy === w.key ? <MiniLoader /> : 'Update'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};

export default WalletUpdater;
